import React, { useEffect, useState } from "react";
import './SideBar.css';
import './SalaryDash.css';
import SalaryProcess from "./SalaryProcess";
import Advance from "./Advance";
import { collection, getDocs, query, where, doc, getDoc, writeBatch, Timestamp, addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from "../firebase";
import Loader from "../components/Loader";
import * as XLSX from 'xlsx';

export default function SalaryDash() {
  const [section, setSection] = useState(() => (window.location.hash ? window.location.hash.replace('#', '') : 'process'));
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const [selectedMonth, setSelectedMonth] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [advanceInitialFilters, setAdvanceInitialFilters] = useState(null);
  const [salaryInitialFilters, setSalaryInitialFilters] = useState(null);
  const [incentives, setIncentives] = useState([]);
  const [incLoading, setIncLoading] = useState(false);
  const [incSearch, setIncSearch] = useState('');
  const [incFrom, setIncFrom] = useState('');
  const [incTo, setIncTo] = useState('');
  const [incCurrentPage, setIncCurrentPage] = useState(1);
  const [incItemsPerPage, setIncItemsPerPage] = useState(10);
  const [editingInc, setEditingInc] = useState(null);
  const [counts, setCounts] = useState({
    process: 0,
    advance: 0,
    incentive: 0
  });

  useEffect(() => {
    const handler = () => setSection(window.location.hash ? window.location.hash.replace('#', '') : 'process');
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);


  // derived filtered incentives for display
  const displayedIncentives = incentives.filter(i => {
    if (incFrom) {
      const fd = new Date(incFrom);
      fd.setHours(0, 0, 0, 0);
      if (!i._dateObj || i._dateObj < fd) return false;
    }
    if (incTo) {
      const td = new Date(incTo);
      td.setHours(23, 59, 59, 999);
      if (!i._dateObj || i._dateObj > td) return false;
    }
    if (incSearch) {
      const q = incSearch.toLowerCase();
      const hay = `${i.ticket || ''} ${i.customer || ''} ${i.remarks || i.remark || ''} ${i.techno || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const incIndexOfLastItem = incCurrentPage * incItemsPerPage;
  const incIndexOfFirstItem = incIndexOfLastItem - incItemsPerPage;
  const currentIncentives = displayedIncentives.slice(incIndexOfFirstItem, incIndexOfLastItem);
  const incTotalPages = Math.ceil(displayedIncentives.length / incItemsPerPage);
  const paginateInc = (pageNumber) => setIncCurrentPage(pageNumber);

  // load incentives for the selected month when user opens incentive section
  const loadIncentives = async () => {
    if (section !== 'incentive') return;
    try {
      setIncLoading(true);
      const [y, m] = selectedMonth.split('-');
      const year = Number(y);
      const month = Number(m);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0, 23, 59, 59, 999);

      let incQuery = query(collection(db, 'incentive'), where('date', '>=', start), where('date', '<=', end));
      let snap = await getDocs(incQuery);
      if (snap.empty) {
        const altQuery = query(collection(db, 'incentives'), where('date', '>=', start), where('date', '<=', end));
        snap = await getDocs(altQuery);
      }

      const itemsRaw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      let items = itemsRaw.map(i => {
        const dateObj = i.date && i.date.toDate ? i.date.toDate() : (i.date instanceof Date ? i.date : (i.date && i.date.seconds ? new Date(i.date.seconds * 1000) : null));
        return { ...i, _dateObj: dateObj, _dateStr: dateObj ? dateObj.toLocaleDateString() : '', amount: Number(i.amount || 0) };
      });

      try {
        const cbIds = [...new Set(items.map(i => i.cashbookId).filter(Boolean))];
        if (cbIds.length) {
          const ticketMap = {};
          await Promise.all(cbIds.map(async id => {
            try {
              const s = await getDoc(doc(db, 'CASHBOOK', id));
              if (s.exists()) ticketMap[id] = s.data().ticket || '';
            } catch (e) { }
          }));
          items = items.map(it => ({ ...it, ticket: it.cashbookId ? (ticketMap[it.cashbookId] || '') : (it.ticket || '') }));
        }
      } catch (e) {
        console.warn('Failed to load linked cashbook tickets', e);
      }

      setIncentives(items);
      setCounts(prev => ({ ...prev, incentive: items.length }));
    } catch (err) {
      console.error('Failed to load incentives for month', err);
      setIncentives([]);
    } finally {
      setIncLoading(false);
    }
  };

  useEffect(() => {
    loadIncentives();

    // Also fetch other counts
    const fetchOtherCounts = async () => {
      try {
        const empSnap = await getDocs(collection(db, "EMPLOYEES"));
        const advSnap = await getDocs(query(collection(db, "advance"), where("status", "==", "pending")));
        setCounts(prev => ({
          ...prev,
          process: empSnap.size,
          advance: advSnap.size
        }));
      } catch (e) {
        console.error("Error fetching other counts for salary dash", e);
      }
    };
    fetchOtherCounts();
  }, [section, selectedMonth]);

  const handleDeleteInc = async (id) => {
    if (!window.confirm("Are you sure you want to delete this incentive?")) return;
    try {
      await deleteDoc(doc(db, 'incentive', id));
      alert("Deleted successfully!");
      loadIncentives();
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed: " + err.message);
    }
  };

  const handleUpdateInc = async (e) => {
    e.preventDefault();
    if (!editingInc) return;
    try {
      const { id, amount, remarks, techno, customer, ticket, date } = editingInc;
      const dateVal = date?.toDate ? date.toDate() : (date instanceof Date ? date : new Date(date));

      await updateDoc(doc(db, 'incentive', id), {
        amount: Number(amount),
        remarks: remarks || '',
        techno: techno || '',
        customer: customer || '',
        ticket: ticket || '',
        updatedAt: new Date()
      });

      alert("Updated successfully!");
      setEditingInc(null);
      loadIncentives();
    } catch (err) {
      console.error("Update failed", err);
      alert("Update failed: " + err.message);
    }
  };

  const openSalaryTab = (opts = {}) => {
    setSalaryInitialFilters(opts);
    window.location.hash = '#process';
    setSection('process');
  };

  const openAdvanceTab = (opts = {}) => {
    setAdvanceInitialFilters(opts);
    window.location.hash = '#advance';
    setSection('advance');
  };

  const handleSyncIncentives = async () => {
    if (!window.confirm(`Sync incentives from Cashbook for ${selectedMonth}? This will create missing incentive records.`)) return;

    setIncLoading(true);
    try {
      const [y, m] = selectedMonth.split('-');
      const year = Number(y);
      const month = Number(m);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0, 23, 59, 59, 999);

      // 1. Get all Cashbook entries for this month with incentive > 0
      const cbQ = query(collection(db, 'CASHBOOK'), where('date', '>=', Timestamp.fromDate(start)), where('date', '<=', Timestamp.fromDate(end)));
      const cbSnap = await getDocs(cbQ);

      const cashbookIncentives = [];
      cbSnap.forEach(doc => {
        const d = doc.data();
        const val = Number(d.incentive || 0);
        if (val > 0) {
          cashbookIncentives.push({
            id: doc.id,
            amount: val,
            date: d.date, // Timestamp
            remarks: d.incentiveremark || '',
            techno: d.techno || '',
            customer: d.customerdetail || '',
            ticket: d.ticket || ''
          });
        }
      });

      // 2. Get existing incentive records for this month
      const incQ = query(collection(db, 'incentive'), where('date', '>=', start), where('date', '<=', end));
      const incSnap = await getDocs(incQ);
      const existingIncentives = incSnap.docs.map(d => d.data());

      // 3. Find missing
      const batch = writeBatch(db);
      let addedCount = 0;

      cashbookIncentives.forEach(cb => {
        // Check if ANY incentive record links to this cashbookId
        const exists = existingIncentives.find(ex => ex.cashbookId === cb.id);
        if (!exists) {
          // Create it
          const ref = doc(collection(db, 'incentive'));
          batch.set(ref, {
            cashbookId: cb.id,
            amount: cb.amount,
            remarks: cb.remarks,
            date: cb.date,
            techno: cb.techno,
            customer: cb.customer,
            createdAt: new Date()
          });
          addedCount++;
        }
      });

      if (addedCount > 0) {
        await batch.commit();
        alert(`Synced! Created ${addedCount} missing incentive records.`);
        // Force reload
        const hash = window.location.hash;
        window.location.hash = '#';
        setTimeout(() => window.location.hash = hash, 10);
      } else {
        alert("All incentives are already synced for this month.");
      }

    } catch (e) {
      console.error("Sync failed", e);
      alert("Sync failed: " + e.message);
    } finally {
      setIncLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-3 px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Salary Dashboard</h3>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${section === 'process' ? 'active' : ''}`}
            onClick={() => { setSection('process'); window.location.hash = '#process'; }}
          >
            Salary Process ({counts.process})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${section === 'advance' ? 'active' : ''}`}
            onClick={() => { setSection('advance'); window.location.hash = '#advance'; }}
          >
            Advance ({counts.advance})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${section === 'incentive' ? 'active' : ''}`}
            onClick={() => { setSection('incentive'); window.location.hash = '#incentive'; }}
          >
            Incentive ({counts.incentive})
          </button>
        </li>
      </ul>

      {section === 'process' && <SalaryProcess initialMonth={salaryInitialFilters?.initialMonth || selectedMonth} initialStatusFilter={salaryInitialFilters?.initialStatusFilter} initialEmployeeFilter={salaryInitialFilters?.initialEmployeeFilter} />}
      {section === 'advance' && <Advance initialFilters={advanceInitialFilters} />}
      {section === 'incentive' && (
        <div>
          <div className="overview-header">
            <div>
              <h5 className="mb-0">Incentives</h5>
              <div className="small-muted">Incentive records for selected month</div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <input type="month" className="form-control form-control-sm" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} />
            </div>
          </div>
          <div className="mt-3">
            <div className="card mb-3 p-2">
              <div className="row g-2 align-items-center">
                <div className="col-md-3">
                  <input className="form-control form-control-sm" placeholder="Search..." value={incSearch} onChange={e => setIncSearch(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={incFrom} onChange={e => setIncFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={incTo} onChange={e => setIncTo(e.target.value)} />
                </div>
                <div className="col-md-5 text-end">
                  <button className="btn btn-sm btn-outline-success me-2" onClick={handleSyncIncentives}>
                    Sync with Cashbook
                  </button>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => {
                    // export filtered incentives
                    const rows = displayedIncentives.map(i => ({ Date: i._dateStr || '', Amount: i.amount || 0, Remarks: i.remarks || i.remark || '', Techno: i.techno || '', Customer: i.customer || '', Ticket: i.ticket || '' }));
                    if (!rows.length) return alert('No incentives to export');
                    const ws = XLSX.utils.json_to_sheet(rows);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Incentives');
                    XLSX.writeFile(wb, `Incentives_${selectedMonth}.xlsx`);
                  }}>Export Excel</button>
                </div>
              </div>
            </div>

            {incLoading ? (
              <Loader />
            ) : (
              <div>
                <div className="mb-2">Total: <strong>{new Intl.NumberFormat().format(incentives.reduce((s, i) => s + (Number(i.amount) || 0), 0))}</strong> ({incentives.length} records)</div>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Remarks</th>
                        <th>Techno</th>
                        <th>Customer</th>
                        <th>Ticket</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentIncentives.length === 0 && <tr><td colSpan={7}>No incentives found for this month.</td></tr>}
                      {currentIncentives.map((inc) => (
                        <tr key={inc.id}>
                          <td>{inc._dateStr || '-'}</td>
                          <td>₹ {new Intl.NumberFormat().format(Number(inc.amount || 0))}</td>
                          <td title={inc.remarks || inc.remark || ''}>{inc.remarks || inc.remark || ''}</td>
                          <td>{inc.techno || ''}</td>
                          <td title={inc.customer || ''}>{inc.customer || ''}</td>
                          <td>{inc.ticket || ''}</td>
                          <td className="text-center">
                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => setEditingInc(inc)}>✏️</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteInc(inc.id)}>🗑️</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {currentIncentives.length > 0 && (
                  <div className={"d-flex justify-content-between align-items-center mt-3 mb-5"}>
                    <div className="d-flex align-items-center gap-2">
                      <span>Show</span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                        value={incItemsPerPage}
                        onChange={(e) => {
                          setIncItemsPerPage(Number(e.target.value));
                          setIncCurrentPage(1);
                        }}
                      >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <span>entries</span>
                      <span className="ms-2 text-muted small">
                        Showing {incIndexOfFirstItem + 1} to {Math.min(incIndexOfLastItem, displayedIncentives.length)} of {displayedIncentives.length}
                      </span>
                    </div>

                    <nav aria-label="Page navigation">
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${incCurrentPage === 1 ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => paginateInc(incCurrentPage - 1)}>Previous</button>
                        </li>
                        {(() => {
                          const totalPages = Math.ceil(displayedIncentives.length / incItemsPerPage);
                          return [...Array(totalPages)].map((_, i) => {
                            const p = i + 1;
                            if (
                              p === 1 ||
                              p === totalPages ||
                              (p >= incCurrentPage - 2 && p <= incCurrentPage + 2)
                            ) {
                              return (
                                <li key={p} className={`page-item ${incCurrentPage === p ? "active" : ""}`}>
                                  <button className="page-link" onClick={() => paginateInc(p)}>{p}</button>
                                </li>
                              );
                            } else if (
                              (p === incCurrentPage - 3 && incCurrentPage > 4) ||
                              (p === incCurrentPage + 3 && incCurrentPage < totalPages - 3)
                            ) {
                              return <li key={p} className="page-item disabled"><span className="page-link">...</span></li>;
                            }
                            return null;
                          });
                        })()}
                        <li className={`page-item ${incCurrentPage === Math.ceil(displayedIncentives.length / incItemsPerPage) ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => paginateInc(incCurrentPage + 1)}>Next</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {editingInc && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Incentive</h5>
                <button type="button" className="btn-close" onClick={() => setEditingInc(null)}></button>
              </div>
              <form onSubmit={handleUpdateInc}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <input type="number" className="form-control" value={editingInc.amount} onChange={e => setEditingInc({ ...editingInc, amount: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea className="form-control" value={editingInc.remarks || editingInc.remark || ''} onChange={e => setEditingInc({ ...editingInc, remarks: e.target.value })} rows="2" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Techno (Name/ID)</label>
                    <input type="text" className="form-control" value={editingInc.techno || ''} onChange={e => setEditingInc({ ...editingInc, techno: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Customer</label>
                    <input type="text" className="form-control" value={editingInc.customer || ''} onChange={e => setEditingInc({ ...editingInc, customer: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Ticket No</label>
                    <input type="text" className="form-control" value={editingInc.ticket || ''} onChange={e => setEditingInc({ ...editingInc, ticket: e.target.value })} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingInc(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div >
  );
}